import { m } from "framer-motion";
import { BazilioLogo, CTAButton } from "@/components/ui-sr";
import { IconArrowDown, IconPhone } from "@/components/ui-sr/icons";
import { SR_EASE_OUT } from "@/lib/motion";
import { siteConfig, telHref, whatsappHref } from "@/site.config";
import type { OpenLeadFn } from "./types";

const HERO_BG =
  "https://d2xsxph8kpxj0f.cloudfront.net/310419663028542953/iGxMMUoFTEBtXEndGQDZHg/hero-premium-jrTMbQRw7TJi2kW8Uxk2FL.webp";

type HeroProps = {
  openLead: OpenLeadFn;
};

export function Hero({ openLead }: HeroProps) {
  return (
    <section
      className="relative w-full min-h-[100svh] flex items-center"
      id="top"
      aria-label="Seção principal. Consórcio sem juros, sem entrada"
    >
      <img
        src={HERO_BG}
        alt=""
        role="presentation"
        className="absolute inset-0 w-full h-full object-cover"
        fetchPriority="high"
        decoding="async"
        width={1920}
        height={1080}
      />

      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/80" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(244,196,48,0.18),transparent_32%)]" />
      <div className="absolute left-1/2 top-[28%] h-48 w-48 -translate-x-1/2 rounded-full bg-[var(--sr-gold)]/18 blur-[110px]" />

      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 pt-28 pb-16">
        <div className="flex flex-col items-center text-center">
          <m.div
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.55, ease: SR_EASE_OUT }}
            className="relative mb-6"
          >
            <div className="absolute inset-x-10 top-6 h-16 rounded-full bg-white/12 blur-3xl" />
            <BazilioLogo
              variant="full"
              tone="dark"
              className="relative h-24 w-auto sm:h-28 lg:h-36 drop-shadow-[0_18px_40px_rgba(0,0,0,0.55)]"
            />
          </m.div>

          <m.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.32, duration: 0.5, ease: SR_EASE_OUT }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/8 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--sr-gold-light)] backdrop-blur-md"
          >
            <span className="sr-pulse-dot" />
            Atendimento consultivo no WhatsApp
          </m.div>

          <m.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.6, ease: SR_EASE_OUT }}
            className="mt-7 max-w-4xl text-[clamp(2.35rem,6vw,4.75rem)] font-bold leading-[0.98] tracking-tight text-white"
          >
            Seu próximo consórcio,
            <span className="block sr-text-gradient-gold">
              sem juros e sem entrada.
            </span>
          </m.h1>

          <m.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.58, duration: 0.55, ease: SR_EASE_OUT }}
            className="mt-5 max-w-2xl text-[clamp(1rem,1.4vw+0.35rem,1.15rem)] leading-relaxed text-white/76"
          >
            Veículos, motos, imóveis, agronegócio, pesados e serviços. A equipe
            Bazilio ajuda na estratégia, acompanha o lance e segue com você até
            a contemplação.
          </m.p>

          <m.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.55, ease: SR_EASE_OUT }}
            className="mt-8 flex w-full max-w-3xl flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center"
          >
            <CTAButton
              variant="red"
              size="xl"
              fullWidthOnMobile
              onClick={() => openLead("hero_primary")}
              className="sm:min-w-[220px]"
            >
              Simular agora
            </CTAButton>
            <CTAButton
              as="a"
              href={whatsappHref()}
              target="_blank"
              rel="noopener noreferrer"
              variant="light-outline"
              size="xl"
              fullWidthOnMobile
              withWhatsAppIcon
              className="sm:min-w-[220px]"
              trackName="whatsapp_clicked"
              trackParams={{ source: "hero_whatsapp" }}
            >
              Falar no WhatsApp
            </CTAButton>
          </m.div>

          <m.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.84, duration: 0.5, ease: SR_EASE_OUT }}
            className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-5"
          >
            <a
              href={telHref()}
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/88 transition hover:text-white"
            >
              <IconPhone className="h-4 w-4 text-[var(--sr-gold-light)]" />
              <span>{siteConfig.brand.whatsapp.displayNumber}</span>
            </a>
            <a
              href="#verticais"
              className="inline-flex items-center gap-2 text-sm font-medium text-white/70 transition hover:text-white"
            >
              <span>Ver tipos de consórcio</span>
              <IconArrowDown className="h-4 w-4" />
            </a>
          </m.div>

          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.98, duration: 0.55 }}
            className="mt-8 flex flex-wrap items-center justify-center gap-3"
          >
            {[
              "Sem entrada",
              "Lance estratégico",
              "Acompanhamento consultivo",
            ].map(item => (
              <span
                key={item}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/25 px-3 py-1.5 text-xs text-white/72 backdrop-blur-sm"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--sr-gold-light)]" />
                {item}
              </span>
            ))}
          </m.div>
        </div>
      </div>
    </section>
  );
}
